import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { hexToHsv, hsvToHex } from '../../customColors.js';

// Selector de color propio (C3D-17), sin el diálogo del navegador: un cuadro de
// saturación/brillo y una barra de tono que se arrastran igual con el dedo que
// con el ratón, y el código hexadecimal para quien ya sabe qué color quiere.

const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

/**
 * Arrastre con captura de puntero: sigue al dedo aunque se salga de la zona, y
 * `touch-action: none` en la zona evita que el arrastre haga scroll del panel.
 * `onMove(x, y)` recibe la posición en 0..1.
 */
const useDrag = (onMove) => {
    const ref = useRef(null);
    const handlers = useRef({ onMove });
    handlers.current = { onMove };

    const read = (event) => {
        const rect = ref.current.getBoundingClientRect();
        handlers.current.onMove(
            clamp01((event.clientX - rect.left) / rect.width),
            clamp01((event.clientY - rect.top) / rect.height),
        );
    };

    return {
        ref,
        onPointerDown: (event) => {
            event.preventDefault();
            ref.current.setPointerCapture?.(event.pointerId);
            read(event);
        },
        onPointerMove: (event) => {
            if (ref.current?.hasPointerCapture?.(event.pointerId)) read(event);
        },
        onPointerUp: (event) => {
            if (!ref.current?.hasPointerCapture?.(event.pointerId)) return;
            ref.current.releasePointerCapture(event.pointerId);
        },
    };
};

/**
 * `value` es el color de partida; `onCommit(hex)` se llama sólo con «Usar»:
 * arrastrar o escribir el código cambia la muestra y nada más, así que se puede
 * probar sin tocar el diseño ni llenar "Tus colores".
 */
const HsvColorPicker = ({ value, onCommit }) => {
    const [hsv, setHsv] = useState(() => hexToHsv(value) || { h: 0, s: 1, v: 1 });
    const hsvRef = useRef(hsv);
    hsvRef.current = hsv;
    const hex = hsvToHex(hsv);
    const [hexDraft, setHexDraft] = useState(hex);

    // Otro color de partida (otra selección): se empieza desde él.
    useEffect(() => {
        const next = hexToHsv(value);
        if (next && hsvToHex(next) !== hsvToHex(hsvRef.current)) setHsv(next);
    }, [value]);

    useEffect(() => { setHexDraft(hex); }, [hex]);

    const commit = () => onCommit(hsvToHex(hsvRef.current));

    const area = useDrag((x, y) => setHsv(current => ({ ...current, s: x, v: 1 - y })));
    const hue = useDrag((x) => setHsv(current => ({ ...current, h: Math.min(x * 360, 359.9) })));

    // El código escrito mueve el selector; un código inválido vuelve al actual.
    const readHex = () => {
        const next = hexToHsv(hexDraft.startsWith('#') ? hexDraft : `#${hexDraft}`);
        if (next) setHsv(next);
        else setHexDraft(hex);
    };

    const pureHue = hsvToHex({ h: hsv.h, s: 1, v: 1 });

    return (
        <div className="flex flex-col gap-3">
            <div
                {...area}
                className="relative h-40 w-full cursor-crosshair touch-none select-none overflow-hidden rounded-lg border border-base-300"
                style={{
                    backgroundColor: pureHue,
                    backgroundImage: 'linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)',
                }}
                role="slider"
                aria-label="Saturación y brillo"
                aria-valuetext={hex}
            >
                <span
                    className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5)]"
                    style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: hex }}
                />
            </div>

            <div
                {...hue}
                className="relative h-5 w-full cursor-pointer touch-none select-none rounded-full border border-base-300"
                style={{ background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)' }}
                role="slider"
                aria-label="Tono"
                aria-valuemin={0}
                aria-valuemax={360}
                aria-valuenow={Math.round(hsv.h)}
            >
                <span
                    className="pointer-events-none absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5)]"
                    style={{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: pureHue }}
                />
            </div>

            <div className="flex items-center gap-2">
                <span
                    className="h-9 w-9 shrink-0 rounded-md border border-base-300"
                    style={{ backgroundColor: hex }}
                    aria-hidden="true"
                />
                <input
                    className="input input-sm input-bordered min-w-0 flex-1 font-mono uppercase"
                    value={hexDraft}
                    maxLength={7}
                    spellCheck={false}
                    aria-label="Código del color"
                    onChange={(event) => setHexDraft(event.target.value)}
                    onBlur={readHex}
                    onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                />
                <button type="button" className="btn btn-sm btn-primary" onClick={commit}>
                    Usar
                </button>
            </div>
        </div>
    );
};

HsvColorPicker.propTypes = {
    value: PropTypes.string,
    onCommit: PropTypes.func.isRequired,
};

export default HsvColorPicker;
